import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CalendarDays } from "lucide-react";
import { CARE_TASKS, MONTHS } from "@/data/site";
import { SectionHead, Reveal } from "./Reveal";
import { scrollToHash, EASE } from "@/lib/scroll";

export const NOW = new Date().getMonth() + 1;

export const requestMonth = (month) => {
  const tasks = CARE_TASKS.filter((t) => t.months.includes(month));
  window.dispatchEvent(new CustomEvent("prefill-inquiry", { detail: { services: tasks.map((t) => t.form), month: MONTHS[month - 1].name } }));
  scrollToHash("#anfrage");
};

const MonthDetail = ({ month }) => {
  const tasks = CARE_TASKS.filter((t) => t.months.includes(month));
  const m = MONTHS[month - 1];
  return (
    <div data-testid="calendar-month-detail" className="h-full rounded-[28px] bg-[#111511] text-white p-7 sm:p-9 flex flex-col relative overflow-hidden">
      <div className="absolute -bottom-24 -right-16 w-72 h-72 rounded-full bg-[#9CC26A]/20 blur-3xl pointer-events-none" />
      <div className="relative flex items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-[#D8E6C4]">
        <CalendarDays className="w-4 h-4" /> {month === NOW ? "Jetzt aktuell" : "Pflege im"}
      </div>
      <motion.h3 key={month} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}
        className="relative mt-3 font-head text-5xl sm:text-6xl font-semibold tracking-[-0.04em]">{m.name}</motion.h3>
      <p className="relative mt-3 text-[#C9CEC6] leading-relaxed">{m.note}</p>
      <ul className="relative mt-7 flex flex-wrap gap-2 flex-1 content-start" data-testid="calendar-month-tasks">
        {tasks.map((t) => (
          <li key={t.key} className="inline-flex items-center gap-2 rounded-full bg-white/[0.08] border border-white/10 px-3.5 py-2 text-sm">
            <t.icon className="w-4 h-4 text-[#9CC26A]" strokeWidth={1.7} /> {t.label}
          </li>
        ))}
      </ul>
      <button data-testid="calendar-request-button" onClick={() => requestMonth(month)}
        className="group relative mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-[#9CC26A] text-[#111511] px-6 py-4 text-[15px] font-semibold transition-colors duration-300 hover:bg-white">
        Pflege für {m.name} anfragen <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </div>
  );
};

const YearMatrix = ({ month, setMonth }) => (
  <div className="overflow-x-auto -mx-5 px-5 sm:mx-0 sm:px-0">
    <table className="w-full min-w-[620px] border-separate border-spacing-y-1.5" data-testid="calendar-year-matrix">
      <thead>
        <tr>
          <th className="w-[150px]" />
          {MONTHS.map((m, i) => (
            <th key={m.short} className="p-0">
              <button onClick={() => setMonth(i + 1)} data-testid={`calendar-month-${i + 1}`}
                className={`w-full rounded-full py-2 text-xs font-semibold transition-colors duration-300 ${month === i + 1 ? "bg-[#111511] text-white" : "text-[#5B615A] hover:bg-[#EEF3E6]"}`}>
                {m.short}{i + 1 === NOW && <span className="block mx-auto mt-0.5 w-1 h-1 rounded-full bg-[#5E8C3A]" />}
              </button>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {CARE_TASKS.map((t) => (
          <tr key={t.key}>
            <td className="pr-3 text-sm font-medium text-[#30352F] whitespace-nowrap"><span className="inline-flex items-center gap-2"><t.icon className="w-4 h-4 text-[#2F5D3A]" strokeWidth={1.7} />{t.label}</span></td>
            {MONTHS.map((_, i) => {
              const on = t.months.includes(i + 1);
              const active = month === i + 1;
              return (
                <td key={i} className="px-0.5">
                  <button onClick={() => setMonth(i + 1)} aria-label={`${t.label} im ${MONTHS[i].name}`}
                    className={`block w-full h-7 rounded-lg transition-colors duration-300 ${on ? (active ? "bg-[#2F5D3A]" : "bg-[#9CC26A]/70 hover:bg-[#9CC26A]") : active ? "bg-[#111511]/[0.07]" : "bg-[#111511]/[0.03]"}`} />
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const CareCalendar = () => {
  const [month, setMonth] = useState(NOW);
  return (
    <section id="pflegekalender" data-testid="care-calendar-section" className="py-24 lg:py-36 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <SectionHead eyebrow="Pflegekalender" lines={["Was im Garten", <span className="text-[#5E8C3A]">wann ansteht.</span>]}
          text="Ihr Jahr im Garten auf einen Blick. Wählen Sie einen Monat – und fragen Sie die passenden Arbeiten direkt an." />
        <div className="mt-14 lg:mt-16 grid lg:grid-cols-12 gap-8 lg:gap-10 items-stretch">
          <Reveal className="lg:col-span-8 order-2 lg:order-1 min-w-0">
            <p className="sm:hidden mb-3 text-xs text-[#5B615A]">Jahresübersicht – seitlich wischen</p>
            <YearMatrix month={month} setMonth={setMonth} />
            <p className="mt-4 text-xs text-[#5B615A]">Richtwerte – je nach Wetter und Garten verschieben sich die Arbeiten etwas.</p>
          </Reveal>
          <Reveal className="lg:col-span-4 order-1 lg:order-2 min-w-0" delay={0.1}>
            <MonthDetail month={month} />
          </Reveal>
        </div>
      </div>
    </section>
  );
};
